import { useMutation, useMutationState, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  analysisSummary,
  isAnalysisBusy,
  type Proposal,
  type RowsQuery,
  type StructuredIngestAnalysisSummary,
  structuredIngestApi,
} from '@/services/api/structuredIngest'
import { errorMessage } from '@/utils/errors'

/** Proposal preparation takes 10–60 s in the background — poll at this cadence while busy. */
const POLL_MS = 2500

export const structuredIngestKeys = {
  readiness: (caseId: string) => ['structured-ingest', 'readiness', caseId] as const,
  list: (caseId: string) => ['structured-ingest', 'list', caseId] as const,
  analyzeDocument: (caseId: string) => ['structured-ingest', 'analyze-document', caseId] as const,
  analysis: (caseId: string, analysisId: string) =>
    ['structured-ingest', 'analysis', caseId, analysisId] as const,
  rows: (caseId: string, analysisId: string, tableId: string, query: RowsQuery | null) =>
    ['structured-ingest', 'rows', caseId, analysisId, tableId, query] as const,
  edgeRows: (caseId: string, relationshipId: string, offset: number, limit: number) =>
    ['structured-ingest', 'edge-rows', caseId, relationshipId, offset, limit] as const,
  explainRow: (
    caseId: string,
    analysisId: string,
    tableId: string,
    recipe: Proposal | null,
    position: number
  ) => ['structured-ingest', 'explain-row', caseId, analysisId, tableId, recipe, position] as const,
}

export function useStructuredIngestReadiness(caseId: string) {
  return useQuery({
    queryKey: structuredIngestKeys.readiness(caseId),
    queryFn: () => structuredIngestApi.readiness(caseId),
    enabled: !!caseId,
    staleTime: 0,
  })
}

export function useStructuredIngestList(caseId: string) {
  return useQuery({
    queryKey: structuredIngestKeys.list(caseId),
    queryFn: () => structuredIngestApi.list(caseId),
    enabled: !!caseId,
    // The documents list shows "mapping…" on fresh uploads — keep it moving.
    refetchInterval: query =>
      query.state.data?.some(s => s.status === 'pending') ? POLL_MS : false,
  })
}

export function useStructuredIngestAnalysis(caseId: string, analysisId: string | null) {
  return useQuery({
    queryKey: structuredIngestKeys.analysis(caseId, analysisId ?? ''),
    queryFn: () => structuredIngestApi.get(caseId, analysisId ?? ''),
    enabled: !!caseId && !!analysisId,
    refetchInterval: query => (isAnalysisBusy(query.state.data) ? POLL_MS : false),
  })
}

export function useAnalyzeStructuredDocument(caseId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: structuredIngestKeys.analyzeDocument(caseId),
    mutationFn: ({ documentId, instructions }: { documentId: string; instructions?: string }) =>
      structuredIngestApi.analyzeDocument(caseId, documentId, instructions),
    onSuccess: analysis => {
      queryClient.setQueryData(structuredIngestKeys.analysis(caseId, analysis.analysisId), analysis)
      // Listed at once, so the documents list shows it preparing instead of offering the start
      // again while the case list is read anew.
      queryClient.setQueryData<StructuredIngestAnalysisSummary[]>(
        structuredIngestKeys.list(caseId),
        list => list && [analysisSummary(analysis), ...list]
      )
      void queryClient.invalidateQueries({ queryKey: structuredIngestKeys.list(caseId) })
    },
  })
}

/**
 * The documents list's one-click start of a mapping for a table that has none, and the documents
 * whose start (from the list or from the document detail) is still on its way.
 */
export function usePrepareMappings(caseId: string) {
  const { t } = useTranslation()
  const { mutateAsync } = useAnalyzeStructuredDocument(caseId)
  const pending = useMutationState({
    filters: { mutationKey: structuredIngestKeys.analyzeDocument(caseId), status: 'pending' },
    select: ({ state }) => (state.variables as { documentId: string }).documentId,
  })
  const starting = useMemo<ReadonlySet<string>>(() => new Set(pending), [pending])
  const start = useCallback(
    (documentId: string) => {
      mutateAsync({ documentId }).catch((error: unknown) =>
        toast.error(t('structuredIngest.prepare.failed'), { description: errorMessage(error, '') })
      )
    },
    [mutateAsync, t]
  )
  return { start, starting }
}

export function useReannotateTable(caseId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      analysisId,
      tableId,
      instructions,
      recipe,
    }: {
      analysisId: string
      tableId: string
      instructions: string
      recipe?: Proposal
    }) => structuredIngestApi.reannotate(caseId, analysisId, tableId, instructions, recipe),
    onMutate: async ({ analysisId }) => {
      await queryClient.cancelQueries({
        queryKey: structuredIngestKeys.analysis(caseId, analysisId),
      })
    },
    onSuccess: analysis => {
      // The table is now pending; the polling query picks up the fresh proposal.
      queryClient.setQueryData(structuredIngestKeys.analysis(caseId, analysis.analysisId), analysis)
      void queryClient.invalidateQueries({ queryKey: structuredIngestKeys.list(caseId) })
    },
  })
}

export function usePreviewTable(caseId: string) {
  return useMutation({
    mutationFn: ({
      analysisId,
      tableId,
      recipe,
    }: {
      analysisId: string
      tableId: string
      recipe: Proposal
    }) => structuredIngestApi.preview(caseId, analysisId, tableId, recipe),
  })
}

/** The row lens — refetched when the row or the (settled) recipe changes; cheap pandas, no model. */
export function useExplainRow(
  caseId: string,
  analysisId: string,
  tableId: string,
  recipe: Proposal | null,
  position: number
) {
  return useQuery({
    queryKey: structuredIngestKeys.explainRow(caseId, analysisId, tableId, recipe, position),
    queryFn: () =>
      structuredIngestApi.explainRow(caseId, analysisId, tableId, recipe ?? {}, position),
    enabled: !!caseId && !!analysisId && !!tableId && recipe !== null,
    staleTime: Number.POSITIVE_INFINITY,
    placeholderData: previous => previous, // keep the last row on screen while the next loads
  })
}

export function useTableRows(
  caseId: string,
  analysisId: string,
  tableId: string,
  query: RowsQuery | null
) {
  return useQuery({
    queryKey: structuredIngestKeys.rows(caseId, analysisId, tableId, query),
    queryFn: () => structuredIngestApi.rows(caseId, analysisId, tableId, query ?? {}),
    enabled: !!caseId && !!analysisId && !!tableId && query !== null,
    staleTime: Number.POSITIVE_INFINITY, // the parquet is immutable
  })
}

export function useEdgeRows(
  caseId: string,
  relationshipId: string | null,
  offset = 0,
  limit = 100
) {
  return useQuery({
    queryKey: structuredIngestKeys.edgeRows(caseId, relationshipId ?? '', offset, limit),
    queryFn: () => structuredIngestApi.edgeRows(caseId, relationshipId ?? '', offset, limit),
    enabled: !!caseId && !!relationshipId,
    staleTime: Number.POSITIVE_INFINITY,
    retry: false, // a 404 means "not a projected edge" — not worth retrying
  })
}

/** Promote selected rows of a table into the graph as one RowExcerpt. */
export function usePromoteRows(caseId: string) {
  return useMutation({
    mutationFn: ({
      analysisId,
      tableId,
      positions,
      note,
    }: {
      analysisId: string
      tableId: string
      positions: number[]
      note?: string
    }) => structuredIngestApi.promoteRows(caseId, analysisId, tableId, { positions, note }),
  })
}

export function useProjectTable(caseId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      analysisId,
      tableId,
      recipe,
    }: {
      analysisId: string
      tableId: string
      recipe: Proposal
    }) => structuredIngestApi.project(caseId, analysisId, tableId, recipe),
    onSuccess: (_result, { analysisId }) => {
      queryClient.invalidateQueries({
        queryKey: structuredIngestKeys.analysis(caseId, analysisId),
      })
      queryClient.invalidateQueries({ queryKey: structuredIngestKeys.list(caseId) })
    },
  })
}
